'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMenuOpen]);

  const navLinks = [
    { href: '/homepage', label: 'Home' },
    { href: '/about', label: 'About' },
    { href: '/social-media-plans', label: 'Social Media Plans' },
    { href: '/contact', label: 'Contact' },
  ];

  const serviceLinks = [
    { href: '/services/social-media-marketing', label: 'Social Media Marketing' },
    { href: '/services/website-development', label: 'Website Development' },
    { href: '/services/app-development', label: 'App Development' },
    { href: '/services/graphic-designing', label: 'Graphic Designing' },
    { href: '/services/festival-greetings', label: 'Festival Greetings' },
    { href: '/services/wedding-invites', label: 'Wedding Invites' },
    { href: '/services/ecatalogue-brochures', label: 'E-Catalogue & Brochures' },
    { href: '/services/print-solutions', label: 'Print Solutions' },
    { href: '/services/product-shoots', label: 'Product Shoots' },
    { href: '/services/logo-designing', label: 'Logo Designing' },
    { href: '/services/whatsapp-bulk-marketing', label: 'WhatsApp Bulk Marketing' },
    { href: '/services/voice-blast-marketing', label: 'Voice Blast Marketing' },
    { href: '/services/ivr-solutions', label: 'IVR Solutions' },
    { href: '/services/chatbot-ai-agents', label: 'Chatbot & AI Agents' },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 transition-all duration-300 px-4 sm:px-6 lg:px-10 py-4">
      <nav
        className={`max-w-7xl mx-auto flex items-center justify-between px-6 py-3 rounded-2xl transition-all duration-300 relative z-50 ${
          isScrolled
            ? 'bg-black/40 backdrop-blur-md border border-zinc-800 shadow-xl'
            : 'bg-black/20 backdrop-blur-sm border border-zinc-800/50'
        }`}
      >
        <div className="flex items-center gap-8">
          <Link href="/homepage" className="flex items-center">
            <AppLogo size={64} />
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`text-sm font-medium transition-colors ${
                  pathname === link?.href
                    ? 'text-amber-500' :'text-zinc-300 hover:text-amber-500'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                  pathname?.startsWith('/services')
                    ? 'text-amber-500' :'text-zinc-300 hover:text-amber-500'
                }`}
              >
                Services
                <Icon name="ChevronDownIcon" size={16} />
              </button>
              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl py-2 max-h-96 overflow-y-auto">
                  {serviceLinks?.map((service) => (
                    <Link
                      key={service?.href}
                      href={service?.href}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        pathname === service?.href
                          ? 'text-amber-500 bg-amber-500/10' :'text-zinc-300 hover:text-amber-500 hover:bg-zinc-800'
                      }`}
                    >
                      {service?.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Link
            href="/contact"
            className="hidden sm:flex px-5 py-2 text-sm font-medium bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all shadow-md"
          >
            Get Started
          </Link>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-zinc-300 hover:text-amber-500 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <Icon name="XMarkIcon" size={24} />
            ) : (
              <Icon name="Bars3Icon" size={24} />
            )}
          </button>
        </div>
      </nav>
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 top-0 left-0 w-full h-full bg-zinc-950/95 backdrop-blur-lg z-40 lg:hidden animate-slide-in-right overflow-y-auto">
          {/* Close Button */}
          <div className="absolute top-6 right-6 z-50">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-3 text-white hover:text-amber-500 transition-colors bg-zinc-800/50 rounded-full backdrop-blur-sm border border-zinc-700"
              aria-label="Close menu"
            >
              <Icon name="XMarkIcon" size={28} />
            </button>
          </div>
          
          <div className="flex flex-col items-center justify-start pt-32 pb-20 gap-6 px-6">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-2xl font-bold uppercase tracking-wider transition-colors ${
                  pathname === link?.href
                    ? 'text-amber-500' :'text-white hover:text-amber-500'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            {/* Mobile Services Section */}
            <div className="w-full max-w-md">
              <h3 className="text-xl font-bold text-amber-500 mb-4 text-center">Services</h3>
              <div className="grid grid-cols-1 gap-3">
                {serviceLinks?.map((service) => (
                  <Link
                    key={service?.href}
                    href={service?.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-base font-medium text-center py-2 px-4 rounded-lg transition-colors ${
                      pathname === service?.href
                        ? 'text-amber-500 bg-amber-500/10' :'text-zinc-300 hover:text-amber-500 hover:bg-zinc-800'
                    }`}
                  >
                    {service?.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              href="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="mt-4 px-8 py-4 text-lg font-bold bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}